import PropTypes from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import { ButtonBase, CircularProgress, Tooltip, Box } from '@mui/material';
import { Icon } from '@iconify/react';

/**
 * Entry point to My Projects (the InteTasks SSO target).
 *
 * Like the Data Access Console this leaves the portal, so it shares that
 * button's shape, height and interaction model. It is deliberately TONAL
 * rather than filled: DataAccessButton is documented as the single filled
 * control in the toolbar, and two competing gradients would leave the
 * toolbar with no focal point at all.
 *
 * Teal, so it is never mistaken for the purple-to-blue Data Access pill
 * sitting beside it.
 */

const BRAND = {
  light: '#0F766E',
  dark: '#2DD4BF',
  darkText: '#5EEAD4',
};

const Root = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }) => {
  const isDark = theme.palette.mode === 'dark';
  const accent = isDark ? BRAND.dark : BRAND.light;
  const text = isDark ? BRAND.darkText : BRAND.light;

  return {
    position: 'relative',
    overflow: 'hidden',
    //MATCHES DataAccessButton EXACTLY , OR THE TWO SIT AT DIFFERENT HEIGHTS IN THE TOOLBAR
    height: 34,
    minWidth: compact ? 34 : 'auto',
    width: compact ? 34 : 'auto',
    padding: compact ? 0 : '0 16px 0 13px',
    borderRadius: 999,
    color: text,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: '0.015em',
    whiteSpace: 'nowrap',
    backgroundColor: alpha(accent, isDark ? 0.14 : 0.09),
    border: '1px solid',
    borderColor: alpha(accent, isDark ? 0.38 : 0.28),
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'box-shadow', 'transform'],
      { duration: 180 },
    ),

    '&:hover': {
      backgroundColor: alpha(accent, isDark ? 0.22 : 0.15),
      borderColor: alpha(accent, isDark ? 0.55 : 0.45),
      transform: 'translateY(-1px)',
      boxShadow: `0 6px 16px -6px ${alpha(accent, 0.5)}`,
    },
    //PRESSED RETURNS IT TO THE SURFACE , SO THE CLICK HAS SOMEWHERE TO GO
    '&:active': {
      transform: 'translateY(0)',
      backgroundColor: alpha(accent, isDark ? 0.28 : 0.2),
      boxShadow: 'none',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${alpha(accent, 0.9)}`,
      outlineOffset: 2,
    },
    '&.Mui-disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
      color: text,
    },

    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover': { transform: 'none' },
    },
  };
});

const Label = styled('span')({
  position: 'relative',
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  lineHeight: 1,
});

export default function InteTasksButton({ loading, onClick, compact = false }) {
  const button = (
    <Root
      compact={compact ? 1 : 0}
      onClick={onClick}
      disabled={loading}
      aria-label="Open My Projects"
      aria-busy={loading || undefined}
    >
      <Label>
        {loading ? (
          <CircularProgress size={15} thickness={5} color="inherit" />
        ) : (
          <Icon icon="mdi:checkbox-marked-circle-outline" width={17} />
        )}
        {!compact && <span>{loading ? 'Opening…' : 'My Projects'}</span>}
      </Label>
    </Root>
  );

  //THE ICON ONLY FORM HAS NO VISIBLE LABEL , SO IT NEEDS THE TOOLTIP TO SAY WHAT IT DOES.
  //THE FULL FORM ALREADY SAYS IT.
  return compact ? (
    <Tooltip title="My Projects" arrow>
      <Box component="span">{button}</Box>
    </Tooltip>
  ) : (
    button
  );
}

InteTasksButton.propTypes = {
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  /** Icon-only, for narrow toolbars. */
  compact: PropTypes.bool,
};
